import { Routes, Route, Navigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

// Import screens
import { HomeScreen } from '../components/screens/home/HomeScreen';
import { LoginScreen } from '../components/screens/auth/LoginScreen';
import { SignUpScreen } from '../components/screens/auth/SignUpScreen';
import { TermsOfServiceScreen } from '../components/screens/auth/TermsOfServiceScreen';
import { PrivacyPolicyScreen } from '../components/screens/auth/PrivacyPolicyScreen';
import { ForgotPasswordScreen } from '../components/screens/auth/ForgotPasswordScreen';
import LogActivityScreen from '../components/screens/activity/LogActivityScreen';
import EditActivityScreen from '../components/screens/activity/EditActivityScreen';
import ProgressScreen from '../components/screens/progress/ProgressScreen';
import { CausesScreen } from '../components/screens/causes/CausesScreen';
import { LevelSelectionScreen } from '../components/screens/onboarding/LevelSelectionScreen';
import ProfileScreen from '../components/screens/profile/ProfileScreen';
import NotificationPreferencesScreen from '../components/screens/profile/settings/NotificationPreferencesScreen';
import PrivacySettingsScreen from '../components/screens/profile/settings/PrivacySettingsScreen';
import HelpSupportScreen from '../components/screens/profile/settings/HelpSupportScreen';
import FeedbackScreen from '../components/screens/profile/settings/FeedbackScreen'; // Import FeedbackScreen
import MyFeedbackScreen from '../components/screens/profile/MyFeedbackScreen'; // Import MyFeedbackScreen
import SharingSuccessScreen from '../components/screens/sharing/SharingSuccessScreen';
import GetInspiredScreen from '../components/screens/inspired/GetInspiredScreen';
import ElevationRequestScreen from '../components/screens/progression/ElevationRequestScreen';
import { AdminFeedback, AdminDeletions } from '../components/admin';
import MyTemplatesScreen from '../components/screens/templates/MyTemplatesScreen';
import PastGoalsScreen from '../components/screens/profile/PastGoalsScreen';
import AdvocacySummaryScreen from '../components/screens/profile/AdvocacySummaryScreen';
import LanguageScreen from '../components/screens/profile/settings/LanguageScreen';
import HowItWorksScreen from '../components/screens/public/HowItWorksScreen';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, isInitialLoad } = useContext(AuthContext);


  if (isInitialLoad) {
    return <div>Loading initial authentication...</div>; // Show a loading indicator during initial auth check
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin-only protected route component
const ProtectedAdminRoute = ({ children }) => {
  const { currentUser, isInitialLoad } = useContext(AuthContext);
  const ADMIN_EMAIL = 'dev@lightandcover.org';

  if (isInitialLoad) {
    return <div>Loading initial authentication...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (currentUser.email !== ADMIN_EMAIL) {
    return <Navigate to="/" />;
  }

  return children;
};

export const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={
        <ProtectedRoute>
          <HomeScreen />
        </ProtectedRoute>
      } />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/signup" element={<SignUpScreen />} />
      <Route path="/terms-of-service" element={<TermsOfServiceScreen />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/how-it-works" element={<HowItWorksScreen />} />
      <Route path="/log-activity" element={
        <ProtectedRoute>
          <LogActivityScreen />
        </ProtectedRoute>
      } />
      <Route path="/edit-activity/:activityId" element={
        <ProtectedRoute>
          <EditActivityScreen />
        </ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute>
          <ProgressScreen />
        </ProtectedRoute>
      } />
      <Route path="/causes" element={
        <ProtectedRoute>
          <CausesScreen />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileScreen />
        </ProtectedRoute>
      } />
      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <NotificationPreferencesScreen />
        </ProtectedRoute>
      } />
      <Route path="/settings/privacy" element={
        <ProtectedRoute>
          <PrivacySettingsScreen />
        </ProtectedRoute>
      } />
      <Route path="/settings/help" element={
        <ProtectedRoute>
          <HelpSupportScreen />
        </ProtectedRoute>
      } />
      <Route path="/settings/language" element={
        <ProtectedRoute>
          <LanguageScreen />
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackScreen />
        </ProtectedRoute>
      } />
      <Route path="/my-feedback" element={
        <ProtectedRoute>
          <MyFeedbackScreen />
        </ProtectedRoute>
      } />
      <Route path="/level-selection" element={
        <ProtectedRoute>
          <LevelSelectionScreen />
        </ProtectedRoute>
      } />
      <Route path="/share-success" element={
        <ProtectedRoute>
          <SharingSuccessScreen />
        </ProtectedRoute>
      } />
      <Route path="/get-inspired" element={
        <ProtectedRoute>
          <GetInspiredScreen />
        </ProtectedRoute>
      } />
      <Route path="/elevation" element={
        <ProtectedRoute>
          <ElevationRequestScreen />
        </ProtectedRoute>
      } />
      <Route path="/my-templates" element={
        <ProtectedRoute>
          <MyTemplatesScreen />
        </ProtectedRoute>
      } />
      <Route path="/advocacy-summary" element={
        <ProtectedRoute>
          <AdvocacySummaryScreen />
        </ProtectedRoute>
      } />
      <Route path="/past-goals" element={
        <ProtectedRoute>
          <PastGoalsScreen />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedAdminRoute>
          <AdminFeedback />
        </ProtectedAdminRoute>
      } />
      <Route path="/admin/deletions" element={
        <ProtectedAdminRoute>
          <AdminDeletions />
        </ProtectedAdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};
